<template>
  <div
    v-if="deals && deals.length > 0"
    v-viewport-width
    class="mb-5 product-carousel"
    :class="{ 'relative-to-container' : relativeToContainer }"
  >
    <div v-if="title" class="product-carousel-title">
      <h3>
        <NuxtLink :to="more">
          <span v-if="!titleImage">{{ title }}</span>
          <img
            v-if="titleImage"
            :src="titleImage"
            :alt="title"
          >
        </NuxtLink>
      </h3>
      <NuxtLink
        v-if="more"
        class="more d-inline"
        :to="more"
        title="Browse this collection"
      >
        View all
      </NuxtLink>
    </div>
    <flicking-carousel :id="id">
      <deal-impression
        v-for="deal in deals"
        :id="deal.id"
        :key="deal.id"
      >
        <div
          class="carousel-cell section-tile"
          :class="{'section-tile-max-width': deals.length > 4}"
        >
          <deal-card-component
            :deal="deal"
            :title="title"
          />
        </div>
      </deal-impression>
    </flicking-carousel>
  </div>
</template>

<script>
import FlickingCarousel from '~/components/FlickingCarousel.vue';
import DealCardComponent from '~/components/common/DealCardComponent.vue';
import DealImpression from '~/components/common/DealImpression.vue';

export default {
  name: 'ProductCarousel',
  components: {
    DealCardComponent,
    DealImpression,
    FlickingCarousel,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: false,
      default: '',
    },
    titleImage: {
      type: String,
      required: false,
      default: '',
    },
    deals: {
      type: Array,
      required: true,
    },
    more: {
      type: String,
      required: false,
      default: '',
    },
    /* jw: controls whether the carousels width will be based on its container or the viewport */
    relativeToContainer: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      carouselOptions: {
        cellAlign: 'left',
        groupCells: true,
        wrapAround: true,
        pageDots: false,
        autoPlay: false,
        contain: true,
        friction: 0.8,
        prevNextButtons: true,
        // Hide disabled buttons
        adaptiveHeight: true,
        on: {
          staticClick: (_e, _pointer, _cellElement, cellIndex) => {
            window.location = this.deals[cellIndex].get_absolute_url;
          },
        },
      },
    };
  },
};
</script>

<style lang="scss" scoped>
@import '~/styles/utils/breakpoints';

$columns-by-breakpoint: (
  sm: 2,
  md: 3,
  lg: 4,
  xl: 5,
);

/*! purgecss ignore */
::v-deep .flicking-arrow-prev,
::v-deep .flicking-arrow-next {
  top: 100px;
}

.product-carousel {
  width: calc(var(--viewport-width, 100vw) - 60px);
  max-width: 1940px;

  &.relative-to-container {
    /* jw: unlike the calculations above, this looks best in its container full width */
    width: 100%;

    /* jw: similarly, since we want the container to control width let's remove the max set above */
    max-width: none;
  }
}

/*! purgecss ignore */
::v-deep .flicking-viewport {
  width: calc(var(--viewport-width, 100vw) - 30px);
  max-width: 1940px;

  .product-carousel.relative-to-container & {
    /* jw: unlike the calculations above, this looks best in its container full width */
    width: 100%;

    /* jw: similarly, since we want the container to control width let's remove the max set above */
    max-width: none;
  }
}

.carousel-cell {
  margin-right: 20px;
  width: 90%;
  border-radius: 4px;
}

.section-tile {
  font-size: 12px;
  padding: 0;
  width: 320px !important;
  max-width: 320px !important;
}

.section-title {
  color: #021414;
  cursor: pointer;
  display: inline-block;
  font-size: 19px;
  font-weight: 600;
  line-height: 22px;
  max-width: 100%;
  overflow: hidden;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.section-title:hover,
.section-title:focus {
  color: #021414;
}

.product-carousel-title {
  margin-bottom: 8px;
  position: relative;
  display: flex;
  align-items: flex-end;
}

.product-carousel-title h3 {
  font-size: 24px;
  font-weight: 700;
  flex-grow: 1;
  margin: 0;
  white-space: nowrap;
  margin-right: 20px;
  text-overflow: ellipsis;
  overflow: hidden;
  line-height: 36px;
}

.product-carousel-title h3 a {
  text-decoration: none;
  color: #021414;
}

.product-carousel-title img {
  max-height: 20px;
}

.product-carousel-title .more {
  font-size: 12px;
  font-weight: 400;
  flex-shrink: 0;
}

@include media-breakpoint-up(sm) {
  .carousel-cell {
    margin-right: 22px;
  }
}

// Generating media queries
@each $breakpoint, $col in $columns-by-breakpoint {
  @include media-breakpoint-up($breakpoint) {
    .carousel-cell {
      width: calc(calc((100% / #{$col}) - 25px));
      margin-bottom: 1px; // Clipping of border due to viewport partial pixel
    }
  }
}

@include media-breakpoint-down(sm) {
  .product-carousel {
    width: calc(var(--viewport-width, 100vw) - 40px);

    &.relative-to-container {
      /* jw: unlike the calculations above, this looks best in its container full width */
      width: 100%;
    }
  }

  /*! purgecss ignore */
  ::v-deep .flicking-viewport {
    width: calc(100% + 20px);
  }

  .section-tile {
    width: 267px !important;
    max-width: 267px !important;
  }
}
</style>
